import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@lib/supabase/client";
import defaultLogo from "@/assets/logo.svg";
import defaultLogoTransparent from "@/assets/logo-transparent.svg";

interface PlatformLogoContextType {
  logoUrl: string;
  transparentLogoUrl: string;
  isLoading: boolean;
  refreshLogo: () => Promise<void>;
}

const PlatformLogoContext = createContext<PlatformLogoContextType>({
  logoUrl: defaultLogo,
  transparentLogoUrl: defaultLogoTransparent,
  isLoading: true,
  refreshLogo: async () => {},
});

export const usePlatformLogo = () => useContext(PlatformLogoContext);

export const PlatformLogoProvider = ({ children }: { children: ReactNode }) => {
  const [logoUrl, setLogoUrl] = useState<string>(defaultLogo);
  const [transparentLogoUrl, setTransparentLogoUrl] = useState<string>(defaultLogoTransparent);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLogo = async () => {
    try {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("setting_value")
        .eq("setting_key", "platform_logo")
        .maybeSingle();

      if (error) {
        console.error("Error fetching platform logo:", error);
        return;
      }

      if (data?.setting_value) {
        const logoSettings = data.setting_value as { url?: string; transparent_url?: string };
        
        // Add cache-busting if URL exists and doesn't already have a timestamp
        if (logoSettings.url) {
          const url = logoSettings.url.includes('?t=') 
            ? logoSettings.url 
            : `${logoSettings.url}?t=${Date.now()}`;
          setLogoUrl(url);
        }
        
        if (logoSettings.transparent_url) {
          const url = logoSettings.transparent_url.includes('?t=') 
            ? logoSettings.transparent_url 
            : `${logoSettings.transparent_url}?t=${Date.now()}`;
          setTransparentLogoUrl(url);
        }
      }
    } catch (error) {
      console.error("Error fetching platform logo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogo();
  }, []);

  const refreshLogo = async () => {
    setIsLoading(true);
    // Force new timestamps on refresh
    await fetchLogo();
  };

  return (
    <PlatformLogoContext.Provider value={{ logoUrl, transparentLogoUrl, isLoading, refreshLogo }}>
      {children}
    </PlatformLogoContext.Provider>
  );
};
